import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Table, Form } from 'react-bootstrap';
import { usePlaidLink } from 'react-plaid-link';
import axios from 'axios';
import SearchableSelect from '../common/SearchableSelect';
import BankIcon from './BankIcon';

const PlaidDiscoveryModal = ({ show, onHide, projectId, onUpdate, connectionId }) => {
  const [linkToken, setLinkToken] = useState(null);
  const [step, setStep] = useState('link'); // 'link' or 'mapping'
  const [discoveredAccounts, setDiscoveredAccounts] = useState([]);
  const [projectAccounts, setProjectAccounts] = useState([]);
  const [accessToken, setAccessToken] = useState(null);
  const [itemId, setItemId] = useState(null);
  const [institution, setInstitution] = useState({ name: '', id: '' });
  const [loading, setLoading] = useState(false);
  const [existingConnections, setExistingConnections] = useState([]);
  const [viewMode, setViewMode] = useState('choice'); // 'choice' or 'link'
  const [activeConnectionId, setActiveConnectionId] = useState(null);

  const [error, setError] = useState(null);

  useEffect(() => {
    if (show) {
      if (connectionId) {
        setActiveConnectionId(connectionId);
        setViewMode('link');
      } else {
        const fetchConnections = async () => {
          try {
            const res = await axios.get(`/api/projects/${projectId}/plaid/connections`);
            setExistingConnections(res.data);
            if (res.data.length === 0) {
              setViewMode('link');
            } else {
              setViewMode('choice');
            }
          } catch (err) {
            console.error('Error fetching connections in modal:', err);
            setViewMode('link');
          }
        };
        fetchConnections();
      }
    }
  }, [show, connectionId, projectId]);

  useEffect(() => {
    if (show && step === 'link' && viewMode === 'link') {
        const fetchLinkToken = async () => {
            setError(null);
            try {
                const url = activeConnectionId 
                    ? `/api/projects/${projectId}/plaid/create_link_token?connectionId=${activeConnectionId}`
                    : `/api/projects/${projectId}/plaid/create_link_token`;
                const res = await axios.post(url);
                setLinkToken(res.data.link_token);
            } catch (err) {
                console.error('Error fetching link token:', err);
                const errMsg = err.response?.data?.error || err.response?.data?.msg || err.message;
                setError(typeof errMsg === 'object' ? JSON.stringify(errMsg) : errMsg);
            }
        };
        fetchLinkToken();
    }
  }, [show, step, projectId, activeConnectionId, viewMode]);

  useEffect(() => {
    if (!show) {
      setStep('link');
      setLinkToken(null);
      setDiscoveredAccounts([]);
      setActiveConnectionId(null);
      setViewMode('choice');
      setError(null);
    }
  }, [show]);

  const fetchProjectAccounts = useCallback(async () => {
    try {
        const res = await axios.get(`/api/projects/${projectId}/accounts`);
        setProjectAccounts(res.data);
    } catch (err) {
        console.error('Error fetching project accounts:', err);
    }
  }, [projectId]);

  const onSuccess = useCallback(async (publicToken, metadata) => {
    setLoading(true);
    try {
        await fetchProjectAccounts();
        let res;
        if (activeConnectionId) {
            res = await axios.post(`/api/projects/${projectId}/plaid/connections/${activeConnectionId}/discover`);
        } else {
            res = await axios.post(`/api/projects/${projectId}/plaid/discover`, { publicToken });
        }
        setAccessToken(res.data.access_token);
        setItemId(res.data.item_id);

        if (metadata.institution) {
            setInstitution({
                name: metadata.institution.name,
                id: metadata.institution.institution_id
            });
        }

        console.log('Discovered Accounts:', res.data.accounts);
        setDiscoveredAccounts(res.data.accounts.map(acc => ({
            ...acc,
            selected: !acc.existingAccountId,
            accountNumber: acc.accountNumber || acc.mask || '',
            type: acc.type === 'depository' ? 'Bank' : acc.type === 'credit' ? 'Credit Card' : 'Bank',
            localMappingId: acc.existingAccountId || acc.suggestedMappingId || ''
        })));
        setStep('mapping');
    } catch (err) {
        console.error('Discovery error:', err);
    } finally {
        setLoading(false);
    }
  }, [projectId, activeConnectionId, fetchProjectAccounts]);

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess,
  });

  const handleFieldChange = (index, field, value) => {
    const updated = [...discoveredAccounts];
    updated[index][field] = value;

    // If mapping to an existing account, update the fields to match it and disable them
    if (field === 'localMappingId' && value) {
        const local = projectAccounts.find(a => a._id === value);
        if (local) {
            updated[index].name = local.name;
            updated[index].accountNumber = local.accountNumber;
            updated[index].type = local.type;
        }
    }
    setDiscoveredAccounts(updated);
  };

  const handleFinalize = async () => {
    setLoading(true);
    try {
        await axios.post(`/api/projects/${projectId}/plaid/finalize-discovery`, {
            accessToken,
            itemId,
            institutionName: institution.name,
            institutionId: institution.id,
            accounts: discoveredAccounts.map(acc => ({
                ...acc,
                // If mapped to local, use that ID for linking
                existingAccountId: acc.localMappingId
            }))
        });
        if (onUpdate) await onUpdate();
        onHide();
        setStep('link');
        setDiscoveredAccounts([]);
    } catch (err) {
        console.error('Finalize error:', err);
    } finally {
        setLoading(false);
    }
  };

  const accountTypes = [
    'Bank', 'Accounts receivable (A/R)', 'Other Current Assets', 'Fixed Assets', 'Other Assets',
    'Accounts payable (A/P)', 'Credit Card', 'Other Current Liabilities', 'Long Term Liabilities',
    'Equity', 'Income', 'Other Income', 'Cost of Goods Sold', 'Expenses', 'Other Expense'
  ];

  const getNameError = (acc, idx) => {
    if (!acc.selected) return null;
    const currentNameClean = (acc.localMappingId && !acc.existingAccountId 
      ? projectAccounts.find(a => a._id === acc.localMappingId)?.name 
      : (acc.existingAccountId ? acc.existingName : acc.name) || ''
    ).toLowerCase().trim();

    if (!currentNameClean) return 'Name is required';

    // 1. Check database collision (exclude the local account currently linked/mapped in this row)
    const dbMatch = projectAccounts.find(a => 
      a.name.toLowerCase().trim() === currentNameClean && 
      a._id !== acc.localMappingId
    );
    if (dbMatch) {
      return `Name already in use (Account #${dbMatch.accountNumber || 'No #'})`;
    }

    // 2. Check intra-modal collision (other checked rows having the same name)
    const duplicateRow = discoveredAccounts.find((other, oIdx) => {
      if (oIdx === idx || !other.selected) return false;
      const otherName = (other.localMappingId && !other.existingAccountId 
        ? projectAccounts.find(a => a._id === other.localMappingId)?.name 
        : (other.existingAccountId ? other.existingName : other.name) || ''
      ).toLowerCase().trim();
      return otherName === currentNameClean;
    });

    if (duplicateRow) {
      return 'Duplicate name in list';
    }

    return null;
  };

  const getMappingWarning = (acc) => {
    if (!acc.localMappingId || acc.existingAccountId) return null;
    const local = projectAccounts.find(a => a._id === acc.localMappingId);
    if (local && local.plaidAccountId && local.plaidAccountId !== acc.plaidAccountId) {
      return '⚠️ Connected to another bank connection. Finalizing will override it.';
    }
    return null;
  };

  const getLocalAccountOptions = (currentRow) => {
    return [
      { value: '', label: '-- Create New Account --' },
      ...projectAccounts.map(a => {
        const isLinkedToOther = a.plaidAccountId && a.plaidAccountId !== currentRow.plaidAccountId;
        return {
          value: a._id,
          label: `${a.name} (${a.accountNumber || 'No #'})${isLinkedToOther ? ' (Already Linked)' : ''}`,
          sublabel: a.type
        };
      })
    ];
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
             {step === 'link' ? (activeConnectionId ? 'Update Bank Connection' : 'Connect to Bank') : 'Link Discovered Accounts'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
         {step === 'link' ? (
          viewMode === 'choice' ? (
            <div className="p-3">
              <h6 className="font-weight-bold mb-4 text-center text-muted" style={{ fontSize: '1.05rem', letterSpacing: '0.5px' }}>
                Choose how you would like to connect your bank
              </h6>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <div 
                    className="card h-100 shadow-sm cursor-pointer border" 
                    style={{ borderRadius: '12px', borderColor: '#e9ecef', transition: 'all 0.25s ease' }}
                    onClick={() => setViewMode('link')}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-3px)';
                      e.currentTarget.style.boxShadow = '0 10px 20px rgba(0,123,255,0.08)';
                      e.currentTarget.style.borderColor = '#007bff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'none';
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.borderColor = '#e9ecef';
                    }}
                  >
                    <div className="card-body text-center p-4 d-flex flex-column align-items-center justify-content-center">
                      <div className="d-flex align-items-center justify-content-center rounded-circle bg-light mb-3" style={{ width: '60px', height: '60px' }}>
                        <i className="fas fa-university fa-2x text-primary"></i>
                      </div>
                      <h5 className="font-weight-bold mb-2 text-dark">Link a New Bank</h5>
                      <p className="small text-muted mb-0">Connect a completely new institution to this project.</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <div className="card h-100 shadow-sm border" style={{ borderRadius: '12px', borderColor: '#e9ecef' }}>
                    <div className="card-body p-4">
                      <h5 className="font-weight-bold mb-3 text-center text-dark">Add to Connected Bank</h5>
                      <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                        {existingConnections.map(conn => (
                          <button 
                            key={conn._id}
                            className="list-group-item list-group-item-action d-flex justify-content-between align-items-center border px-3 py-2 rounded mb-2 hover-shadow"
                            style={{ border: '1px solid #e9ecef', borderRadius: '8px', transition: 'all 0.2s ease' }}
                            onClick={() => {
                              setActiveConnectionId(conn._id);
                              setViewMode('link');
                            }}
                          >
                            <div className="d-flex align-items-center">
                              <div className="mr-3">
                                <BankIcon institutionName={conn.institutionName} logo={conn.logo} size="36px" />
                              </div>
                              <div className="text-left">
                                <span className="font-weight-bold text-dark d-block" style={{ fontSize: '0.9rem' }}>{conn.institutionName}</span>
                                <span className="small text-muted">{conn.accounts?.length || 0} account(s) linked</span>
                              </div>
                            </div>
                            <span className="badge badge-primary px-3 py-2" style={{ borderRadius: '6px', fontSize: '0.75rem' }}>
                              Add Accounts <i className="fas fa-arrow-right ml-1"></i>
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center p-4">
              {!activeConnectionId && existingConnections.length > 0 && (
                <div className="alert alert-warning mb-4 text-left p-3 mx-auto" style={{ borderRadius: '8px', maxWidth: '600px', borderLeftWidth: '4px', borderLeftColor: '#ffc107' }}>
                  <h6 className="font-weight-bold text-dark mb-2">
                    <i className="fas fa-exclamation-triangle mr-2 text-warning"></i> Avoid Redundant Plaid Fees
                  </h6>
                  <p className="small mb-0 text-muted" style={{ lineHeight: '1.4' }}>
                    If you are trying to link more accounts or re-authenticate an already-connected bank (such as <strong>{existingConnections.map(c => c.institutionName).join(', ')}</strong>), 
                    please go back and click <strong>"Add Accounts"</strong>.
                    Creating a new connection for an already connected bank will incur redundant Plaid setup charges.
                  </p>
                </div>
              )}
              {error && (
                <div className="alert alert-warning mb-4 text-left p-3 mx-auto" style={{ borderRadius: '8px', maxWidth: '600px', borderLeftWidth: '4px', borderLeftColor: '#ffc107' }}>
                  <h6 className="font-weight-bold text-dark mb-2">
                    <i className="fas fa-exclamation-triangle mr-2 text-warning"></i> Plaid Keys Configuration Required
                  </h6>
                  <p className="small mb-0 text-muted" style={{ lineHeight: '1.4' }}>
                    {error.includes('configured') ? 'Plaid API keys are missing. Ask your administrator to configure keys under Admin Portal -> Organization -> Plaid API Keys tab.' : error}
                  </p>
                </div>
              )}
              <p className="mb-4">Connect your bank accounts securely via Plaid to discover and import them.</p>
              <div className="d-flex justify-content-center align-items-center">
                {!connectionId && existingConnections.length > 0 && (
                  <Button 
                    variant="outline-secondary" 
                    className="mr-3 px-4 py-2 font-weight-bold" 
                    style={{ borderRadius: '8px' }}
                    onClick={() => {
                      setViewMode('choice');
                      setActiveConnectionId(null);
                      setLinkToken(null);
                    }}
                  >
                    <i className="fas fa-chevron-left mr-2"></i> Back
                  </Button>
                )}
                <Button 
                  variant="primary" 
                  className="px-4 py-2 font-weight-bold"
                  style={{ borderRadius: '8px' }}
                  onClick={() => open()} 
                  disabled={!ready || loading || !!error}
                >
                    {loading ? 'Processing...' : 'Start Connection Flow'}
                </Button>
              </div>
            </div>
          )
        ) : (
          <div>
            <p className="small text-muted mb-3">
                Review the accounts found. You can link them to existing accounts in your app or create new ones.
            </p>
            <Table responsive size="sm" className="discovery-table">
                <thead>
                    <tr>
                        <th width="40">Import</th>
                        <th>Bank Account (Plaid)</th>
                        <th width="250">Link to Existing Account</th>
                        <th>Display Name</th>
                        <th>Account Number</th>
                        <th>Account Type</th>
                    </tr>
                </thead>
                <tbody>
                    {discoveredAccounts.map((acc, idx) => (
                        <tr key={acc.plaidAccountId} className={acc.existingAccountId ? 'bg-light' : ''}>
                            <td className="text-center">
                                <Form.Check 
                                    type="checkbox" 
                                    checked={acc.selected} 
                                    onChange={(e) => handleFieldChange(idx, 'selected', e.target.checked)}
                                    disabled={acc.existingAccountId}
                                />
                            </td>
                            <td className="small align-middle">
                                <div className="font-weight-bold">{acc.name}</div>
                                <div className="text-muted">Mask: {acc.mask} | {acc.type}</div>
                                {acc.existingAccountId && <div className="text-info x-small font-weight-bold mt-1"><i className="fas fa-link mr-1"></i> Already Linked</div>}
                            </td>
                             <td>
                                <SearchableSelect
                                    options={getLocalAccountOptions(acc)}
                                    value={acc.localMappingId}
                                    onChange={(e) => handleFieldChange(idx, 'localMappingId', e.target.value)}
                                    disabled={!acc.selected || acc.existingAccountId}
                                    placeholder="Search existing accounts..."
                                    className="sm"
                                />
                                {getMappingWarning(acc) && (
                                    <div className="text-warning mt-1 font-weight-bold" style={{ fontSize: '0.65rem' }}>
                                        {getMappingWarning(acc)}
                                    </div>
                                )}
                            </td>
                            <td>
                                <Form.Control 
                                    size="sm" 
                                    value={acc.localMappingId && !acc.existingAccountId ? projectAccounts.find(a => a._id === acc.localMappingId)?.name : (acc.existingAccountId ? acc.existingName : acc.name)} 
                                    onChange={(e) => handleFieldChange(idx, 'name', e.target.value)}
                                    disabled={!acc.selected || acc.existingAccountId || acc.localMappingId}
                                    className={acc.existingAccountId || acc.localMappingId ? 'border-0 bg-transparent font-weight-bold mb-0' : 'mb-0'}
                                    isInvalid={!!getNameError(acc, idx)}
                                />
                                {getNameError(acc, idx) && (
                                    <div className="text-danger mt-1" style={{ fontSize: '0.65rem', fontWeight: 'bold' }}>
                                        {getNameError(acc, idx)}
                                    </div>
                                )}
                            </td>
                            <td>
                                <Form.Control 
                                    size="sm" 
                                    value={acc.localMappingId && !acc.existingAccountId ? projectAccounts.find(a => a._id === acc.localMappingId)?.accountNumber : (acc.existingAccountId ? (acc.accountNumber || acc.mask) : acc.accountNumber)} 
                                    onChange={(e) => handleFieldChange(idx, 'accountNumber', e.target.value)}
                                    disabled={!acc.selected || acc.existingAccountId || acc.localMappingId}
                                    className={acc.existingAccountId || acc.localMappingId ? 'border-0 bg-transparent' : ''}
                                />
                            </td>
                            <td>
                                {acc.existingAccountId || acc.localMappingId ? (
                                    <div className="small font-weight-bold py-1">
                                        {acc.localMappingId ? projectAccounts.find(a => a._id === acc.localMappingId)?.type : acc.type}
                                    </div>
                                ) : (
                                    <Form.Control 
                                        as="select" 
                                        size="sm" 
                                        value={acc.type}
                                        onChange={(e) => handleFieldChange(idx, 'type', e.target.value)}
                                        disabled={!acc.selected}
                                    >
                                        {accountTypes.map(t => <option key={t} value={t}>{t}</option>)}
                                    </Form.Control>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Cancel</Button>
        {step === 'mapping' && (
            <Button 
                variant="primary" 
                onClick={handleFinalize} 
                disabled={loading || discoveredAccounts.some((acc, idx) => getNameError(acc, idx))}
            >
                {loading ? 'Saving...' : 'Finish Discovery'}
            </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default PlaidDiscoveryModal;

